/**
 * 1.7.8 (D-1.7.8-2) — Move on a card of today: the When control (no Now — the free gap first when there is one),
 * How long, one optional line (why), Save; the overlap step (D-1.7.8-3: Push · Keep both · Cancel); then Who needs
 * to know. Rendered inside a <Sheet>. Times are still labels: the move is the app's own label for this visit today —
 * its position, its hold and its confirmation do not change. Another day stays Reschedule (Office action).
 */
import { useState, type RefObject } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TodayVisit } from '@shared/api-types';
import type { DayStartRequest, OverlapAnswer, ToldRow } from '@shared/day-bend';
import { isPastStart, windowWords } from '@shared/day-bend';
import { VISIT_LENGTH_STEPS, clockWithPeriod, dayPlanFor, formatClock, lengthWords } from '@shared/dayplan';
import { serviceMinutesNow } from '@shared/dates';
import { ApiRequestError } from '@/lib/api';
import { useDayPlan } from '@/lib/queries';
import { useDirtyFlag } from '@/lib/dirty';
import { SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/Icons';
import { useAnnounce } from '@/components/Announcer';
import { useToast } from '@/components/ui/use-toast';
import { moveInDayReq, overlapOf } from './api';
import { bendStateOf } from './day';
import { WhenControl, type WhenValue } from './WhenControl';
import { OverlapChoice } from './OverlapChoice';
import { WhoNeedsToKnow } from './WhoNeedsToKnow';

export interface MoveInDaySheetProps {
  visit: TodayVisit;
  /** The card's window now, minutes after midnight (null when the day has none for it). */
  window: { start: number; end: number } | null;
  /** The free gap on the day, if any: offered first. */
  gap?: { from: number; to: number } | null;
  returnFocusTo?: RefObject<HTMLElement | null>;
  onDone: () => void;
}

export function MoveInDaySheet({ visit, window: win, gap, returnFocusTo, onDone }: MoveInDaySheetProps) {
  const { t, i18n } = useTranslation();
  const lang: 'en' | 'es' = i18n.language.startsWith('es') ? 'es' : 'en';
  const announce = useAnnounce();
  const toast = useToast();
  const qc = useQueryClient();
  const settings = useDayPlan().data?.settings;
  const day = visit.serviceDateKey;
  const dayEnd = dayPlanFor(day, settings ?? {}).endMinutes;

  const length0 = visit.lengthMinutes ?? (win ? win.end - win.start : VISIT_LENGTH_STEPS[1]!);
  const lengths = VISIT_LENGTH_STEPS.includes(length0) ? VISIT_LENGTH_STEPS : [...VISIT_LENGTH_STEPS, length0].sort((a, b) => a - b);
  /** Nothing is chosen until the person chooses: Save waits for a time. */
  const [when, setWhen] = useState<number | null>(null);
  const [minutes, setMinutes] = useState(length0);
  const [why, setWhy] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answer, setAnswer] = useState<OverlapAnswer | null>(null);
  const [toTell, setToTell] = useState<ToldRow[] | null>(null);
  useDirtyFlag('move-day', when !== null || why !== '' || busy);

  const started = bendStateOf(visit) === 'started';
  const startedAt = visit.arrival?.at ? serviceMinutesNow(new Date(visit.arrival.at)) : (win?.start ?? null);
  const planned = visit.chosen?.start ?? null;
  const pastEnd = when !== null && when + minutes > dayEnd ? when + minutes : null;
  // 14 Oct 2026: a time that has passed (and the visit not started) is the office putting it on the record: a reason
  const pastNeedsWhy = !started && when !== null && isPastStart(when, serviceMinutesNow()) && !why.trim();
  const value: WhenValue = when === null ? { mode: 'now' } : { mode: 'time', minutes: when };

  const save = async (onOverlap?: 'push' | 'keep' | 'swap') => {
    if (when === null) return;
    setBusy(true);
    setError(null);
    const body: DayStartRequest = {
      appointmentId: visit.appointmentId,
      start: when,
      ...(minutes !== length0 ? { minutes } : {}),
      ...(why.trim() ? { why: why.trim() } : {}),
      ...(onOverlap ? { onOverlap } : {}),
    };
    try {
      const res = await moveInDayReq(body);
      void qc.invalidateQueries({ queryKey: ['today'] });
      const said = [
        t(res.unchanged ? 'when178.move.unchanged' : 'when178.move.moved', { unit: visit.unit, time: formatClock(res.start) }),
        ...(res.moved.length ? [t('when178.move.pushed', { count: res.moved.length })] : []),
        ...(res.kept ? [t('when178.move.kept', { unit: res.kept.unit, by: res.kept.by })] : []),
        ...(res.endsAfterDayEnd ? [t('when178.move.pastEndSaved', { end: clockWithPeriod(res.endsAfterDayEnd.end, lang), dayEnd: clockWithPeriod(res.endsAfterDayEnd.dayEnd, lang) })] : []),
      ].join(' · ');
      toast(said);
      announce(said);
      setAnswer(null);
      if (res.toTell.length) setToTell(res.toTell);
      else onDone();
    } catch (e) {
      const choices = overlapOf(e);
      if (choices) {
        setAnswer(choices);
      } else {
        // any other refusal (finished, not today, cannot push …): back to the form with the server's own words
        const m = e instanceof ApiRequestError ? (e.body?.message ?? e.message) : (e as Error).message;
        setAnswer(null);
        setError(m);
        announce(m, 'assertive');
      }
    } finally {
      setBusy(false);
    }
  };

  const step = toTell ? 'told' : answer ? 'overlap' : 'form';

  return (
    <SheetContent
      title={t('when178.move.title', { unit: visit.unit })}
      returnFocusTo={returnFocusTo}
      data-testid="move-day-sheet"
      data-step={step}
      footer={
        step === 'form' ? (
          <>
            <Button variant="secondary" type="button" onClick={onDone} data-testid="move-day-cancel">
              {t('when178.move.cancel')}
            </Button>
            <Button type="button" disabled={when === null || busy || pastNeedsWhy} onClick={() => save()} doneLabel={t('busy1024.sent')} data-testid="move-day-save">
              {busy ? t('when178.move.saving') : t('when178.move.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      {step === 'told' ? (
        <WhoNeedsToKnow rows={toTell ?? []} onDone={onDone} />
      ) : step === 'overlap' ? (
        <OverlapChoice answer={answer!} unit={visit.unit} busy={busy} onPush={() => void save('push')} onSwap={() => void save('swap')} onKeep={() => void save('keep')} onCancel={() => setAnswer(null)} />
      ) : (
        <div className="move-day178">
          <p className="help" data-testid="move-day-now">
            <b>{visit.unit}</b> · {visit.tenantName} ·{' '}
            {started && startedAt !== null
              ? planned !== null
                ? t('when178.move.started', { planned: formatClock(planned), started: formatClock(startedAt) })
                : t('when178.move.startedOnly', { started: formatClock(startedAt) })
              : win
                ? t('when178.move.nowAt', { window: windowWords(win) })
                : null}
          </p>
          <WhenControl
            id="move-day-when"
            day={day}
            value={value}
            onChange={(v) => setWhen(v.mode === 'time' ? v.minutes : null)}
            nowLabel=""
            allowFuture
            suggest={gap ? { minutes: gap.from, label: t('when178.move.freeFrom', { time: formatClock(gap.from) }) } : null}
          />
          <div className="field">
            <label htmlFor="move-day-length">{t('when178.move.length')}</label>
            <select id="move-day-length" className="input" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} data-testid="move-day-length">
              {lengths.map((m) => (
                <option key={m} value={m}>
                  {lengthWords(m)}
                </option>
              ))}
            </select>
          </div>
          {pastEnd !== null ? (
            <p className="past-end-line" role="status" data-testid="move-day-past-end">
              <Icon name="alert" aria-hidden="true" />
              <span>{t('when178.move.pastEnd', { end: clockWithPeriod(pastEnd, lang), dayEnd: clockWithPeriod(dayEnd, lang) })}</span>
            </p>
          ) : null}
          <div className="field">
            <label htmlFor="move-day-why">{t('when178.move.why')}</label>
            <input id="move-day-why" className="input" maxLength={200} value={why} onChange={(e) => setWhy(e.target.value)} data-testid="move-day-why" />
            {pastNeedsWhy ? (
              <p className="help" role="status" data-testid="move-day-past-why">
                {t('book.pastWhy')}
              </p>
            ) : null}
          </div>
          <p className="help">{t('when178.move.otherDay')}</p>
          {error ? (
            <div className="warn take" role="alert" data-testid="move-day-error">
              <Icon name="alert" aria-hidden="true" />
              <div>{error}</div>
            </div>
          ) : null}
        </div>
      )}
    </SheetContent>
  );
}
